/**
 * Phase 4 slice 3 — Cube Tests list. Filters + record button + click-through.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FlaskConical } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const GRADES = ['M15', 'M20', 'M25', 'M30', 'M35', 'M40', 'M45', 'M50', 'M60', 'M70', 'Other'];
const STATUS_TONE = {
  recorded: 'bg-slate-100 text-slate-600 border-slate-200',
  under_review: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  non_conforming: 'bg-red-50 text-red-700 border-red-200',
  draft: 'bg-slate-100 text-slate-500 border-slate-200',
};
import { formatDate as fmt } from '../../lib/format';

const Acceptance = ({ met }) => {
  if (met === true) return <span className="tag border bg-emerald-50 text-emerald-700 border-emerald-200">Pass</span>;
  if (met === false) return <span className="tag border bg-red-50 text-red-700 border-red-200">Fail</span>;
  return <span className="tag border bg-slate-100 text-slate-500 border-slate-200">Pending</span>;
};

export default function CubeTestList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState('');
  const [rows, setRows] = useState([]);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);

  useEffect(() => {
    const q = [projectId && `project_id=${projectId}`, grade && `grade=${grade}`, status && `status=${status}`].filter(Boolean).join('&');
    apiGet(`/quality/cube-tests${q ? `?${q}` : ''}`).then((d) => setRows(d.cube_tests || [])).catch(() => {});
  }, [projectId, grade, status]);

  return (
    <div>
      <PageHeader overline="Quality" title="Cube Tests" subtitle="Concrete compressive-strength tests with IS-456 acceptance"
        actions={(
          <button onClick={() => navigate('/app/quality/cube-tests/new')} className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2f3a5e]" data-testid="ct-record-nav">
            <Plus size={14} /> Record Cube Test
          </button>
        )}
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]" data-testid="ct-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </select>
        <select value={grade} onChange={(e) => setGrade(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="ct-filter-grade">
          <option value="">All grades</option>
          {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid="ct-filter-status">
          <option value="">All statuses</option>
          {['recorded', 'under_review', 'approved', 'non_conforming'].map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full dense-table" data-testid="cube-test-table">
            <thead><tr><th>Pour</th><th>Grade</th><th>Location</th><th>Batch</th><th className="text-center">Cubes</th><th className="text-center">28d Result</th><th>Status</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10"><FlaskConical size={20} className="inline mr-2 opacity-40" />No cube tests recorded.</td></tr>
                : rows.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/quality/cube-tests/${c.id}`)} data-testid={`ct-row-${c.id}`}>
                    <td className="text-xs text-slate-600">{fmt(c.pour_date)}</td>
                    <td><span className="font-display font-bold text-[13px]">{c.concrete_grade === 'Other' ? (c.concrete_grade_other || 'Other') : c.concrete_grade}</span></td>
                    <td className="text-[13px] max-w-[220px] truncate">{c.pour_location_on_site || '—'}</td>
                    <td className="text-xs text-slate-500">{c.batch_number || '—'}</td>
                    <td className="text-center">{(c.cube_set || []).length}</td>
                    <td className="text-center"><Acceptance met={c.acceptance_criteria_met} /></td>
                    <td><span className={`tag border ${STATUS_TONE[c.status] || STATUS_TONE.recorded}`}>{(c.status || '').replace('_', ' ')}</span></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
